import { Box, Button, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import heroGiias from '../Asset/1.webp';
import PageSeo, {
  articleJsonLd,
  breadcrumbJsonLd,
  keywordSets,
  localBusinessJsonLd,
} from '../PageSeo';

const waLink =
  'https://api.whatsapp.com/send?phone=6281286864825&text=Halo%20Junius%2C%20saya%20ingin%20info%20Promo%20GIIAS%20BYD%202026%20tanggal%2029%20Juli%2C%20termasuk%20bunga%200%25%20dan%20special%20price';

const promoBenefits = [
  {
    number: '01',
    title: 'Bunga spesial 1-5 tahun',
    text: 'Pilihan tenor yang lebih fleksibel membantu calon pembeli menyesuaikan cicilan dengan rencana keuangan dan kebutuhan pemakaian mobil.',
  },
  {
    number: '02',
    title: 'Program bunga 0%',
    text: 'Tersedia peluang program bunga 0% dengan ketentuan model, tenor, uang muka, leasing, dan persetujuan kredit yang berlaku.',
  },
  {
    number: '03',
    title: 'Special gift',
    text: 'Benefit tambahan berupa hadiah khusus dapat melengkapi paket pembelian selama program GIIAS 2026 berlangsung.',
  },
  {
    number: '04',
    title: 'Priority unit',
    text: 'Prioritas unit membantu proses pemilihan dan alokasi kendaraan, terutama untuk model, varian, atau warna yang banyak diminati.',
  },
  {
    number: '05',
    title: 'Merchandise GIIAS',
    text: 'Calon pembeli berkesempatan memperoleh merchandise khusus sebagai bagian dari program pembelian GIIAS.',
  },
  {
    number: '06',
    title: 'Special price',
    text: 'Tanyakan penawaran harga khusus untuk model BYD pilihan Anda dan bandingkan dengan skema pembiayaan yang tersedia.',
  },
];

const claimSteps = [
  'Tentukan model BYD, varian, dan warna yang paling sesuai dengan kebutuhan.',
  'Hubungi Junius untuk mengecek program promo yang berlaku pada model tersebut.',
  'Bandingkan tenor 1-5 tahun, uang muka, cicilan, serta ketentuan program bunga 0%.',
  'Konfirmasi stok dan priority unit sebelum melanjutkan pemesanan.',
];

const faqItems = [
  {
    question: 'Kapan Promo GIIAS BYD 2026 tersedia?',
    answer:
      'Informasi Promo GIIAS BYD 2026 pada halaman ini berlaku untuk tanggal 29 Juli 2026. Hubungi Junius untuk memastikan jam layanan, ketersediaan unit, dan ketentuan program terbaru.',
  },
  {
    question: 'Apa saja benefit Promo GIIAS BYD 2026?',
    answer:
      'Benefit yang tersedia meliputi bunga spesial tenor 1-5 tahun, program bunga 0%, special gift, priority unit, merchandise, dan special price.',
  },
  {
    question: 'Apakah bunga 0% berlaku untuk semua mobil BYD?',
    answer:
      'Belum tentu. Ketersediaan bunga 0% dapat bergantung pada model, tenor, uang muka, leasing, dokumen, dan persetujuan kredit. Konfirmasikan simulasi untuk model yang Anda pilih.',
  },
  {
    question: 'Bagaimana cara mendapatkan Promo GIIAS BYD?',
    answer:
      'Pilih model BYD yang diminati, lalu hubungi Junius di BYD BSD Serpong untuk mengecek promo, simulasi pembiayaan, stok, priority unit, dan proses pemesanan.',
  },
];

const faqJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqItems.map((item) => ({
    '@type': 'Question',
    name: item.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: item.answer,
    },
  })),
};

function PromoGiias2026Detail() {
  return (
    <>
      <PageSeo
        title='Promo GIIAS BYD 2026 | Bunga 0% & Special Price'
        description='Promo GIIAS BYD 29 Juli 2026: bunga spesial 1-5 tahun, bunga 0%, special gift, priority unit, merchandise, dan special price. Cek syaratnya.'
        keywords={[
          ...keywordSets.localDealer,
          'Promo GIIAS BYD 2026',
          'promo BYD GIIAS',
          'promo BYD 29 Juli 2026',
          'bunga 0 persen BYD',
          'special price BYD GIIAS',
          'promo mobil listrik GIIAS 2026',
          'promo BYD BSD Serpong',
          'promo BYD Tangerang',
        ]}
        path='/blog/promo-giias-byd-2026'
        image={heroGiias}
        type='article'
        jsonLd={[
          localBusinessJsonLd(),
          articleJsonLd({
            title: 'Promo GIIAS BYD 2026 | Bunga 0% & Special Price',
            description:
              'Panduan Promo GIIAS BYD tanggal 29 Juli 2026, meliputi bunga spesial 1-5 tahun, bunga 0%, special gift, priority unit, merchandise, dan special price.',
            image: heroGiias,
            path: '/blog/promo-giias-byd-2026',
            datePublished: '2026-07-24',
            dateModified: '2026-07-24',
          }),
          breadcrumbJsonLd([
            { name: 'Beranda', path: '/' },
            { name: 'Blog', path: '/blog' },
            {
              name: 'Promo GIIAS BYD 2026',
              path: '/blog/promo-giias-byd-2026',
            },
          ]),
          faqJsonLd,
        ]}
      />

      <Box component='main' sx={{ backgroundColor: '#f4f7fb', overflow: 'hidden' }}>
        <HeroSection />
        <IntroSection />
        <BenefitSection />
        <FinancingSection />
        <ClaimSection />
        <FaqSection />
        <CtaSection />
      </Box>
    </>
  );
}

function HeroSection() {
  return (
    <Box
      component='section'
      aria-labelledby='promo-giias-title'
      sx={{
        position: 'relative',
        minHeight: { xs: '88vh', md: '94vh' },
        display: 'flex',
        alignItems: 'flex-end',
        pt: { xs: 10, md: 11 },
        backgroundColor: '#071a36',
      }}
    >
      <Box
        component='img'
        src={heroGiias}
        alt='Promo GIIAS BYD 2026 tanggal 29 Juli'
        sx={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          objectPosition: 'center',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background:
            'linear-gradient(180deg, rgba(7,26,54,0.2) 0%, rgba(7,26,54,0.42) 42%, rgba(7,26,54,0.96) 100%)',
        }}
      />
      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          width: '100%',
          maxWidth: 1180,
          mx: 'auto',
          px: { xs: 2, md: 5 },
          pb: { xs: 5, md: 7 },
          color: '#fff',
        }}
      >
        <Typography sx={dateBadge}>29 Juli 2026</Typography>
        <Typography id='promo-giias-title' component='h1' sx={heroTitle}>
          Promo GIIAS BYD 2026
        </Typography>
        <Typography sx={heroText}>
          Momentum untuk mendapatkan bunga spesial, program bunga 0%, hadiah,
          prioritas unit, merchandise, dan special price saat memilih mobil BYD.
        </Typography>
        <Box sx={{ mt: 3, display: 'flex', gap: 1.3, flexWrap: 'wrap' }}>
          <Button
            href={waLink}
            target='_blank'
            rel='noopener noreferrer'
            variant='contained'
            sx={buttonPrimary}
          >
            Cek Promo via WhatsApp
          </Button>
          <Button
            component={RouterLink}
            to='/pricing'
            variant='outlined'
            sx={buttonGhost}
          >
            Lihat Harga BYD
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

function IntroSection() {
  return (
    <Box component='article' sx={{ px: { xs: 2, md: 6 }, py: { xs: 7, md: 9 }, backgroundColor: '#fff' }}>
      <Box sx={{ maxWidth: 920, mx: 'auto' }}>
        <Typography sx={eyebrow}>Penawaran GIIAS 2026</Typography>
        <Typography component='h2' sx={sectionTitle}>
          Lebih dari sekadar potongan harga
        </Typography>
        <Typography sx={{ ...bodyText, mt: 1.5 }}>
          Promo GIIAS BYD 2026 pada 29 Juli dirancang untuk memberi lebih banyak
          pilihan kepada calon pembeli. Ada benefit pembiayaan untuk membantu
          mengatur cicilan, sekaligus keuntungan non-pembiayaan seperti hadiah,
          merchandise, dan prioritas unit.
        </Typography>
        <Typography sx={{ ...bodyText, mt: 1.2 }}>
          Kunci memilih promo bukan hanya mencari angka cicilan paling rendah.
          Perhatikan juga tenor, uang muka, total pembayaran, model yang
          memenuhi syarat, serta kesiapan unit. Dengan membandingkan seluruh
          komponen tersebut, Anda bisa memilih program yang lebih sesuai dengan
          kebutuhan dan kemampuan keuangan.
        </Typography>
      </Box>
    </Box>
  );
}

function BenefitSection() {
  return (
    <Box
      component='section'
      aria-labelledby='benefit-giias-title'
      sx={{ px: { xs: 2, md: 6 }, py: { xs: 7, md: 9 } }}
    >
      <Box sx={{ maxWidth: 1180, mx: 'auto' }}>
        <Typography sx={eyebrow}>Enam Benefit Utama</Typography>
        <Typography id='benefit-giias-title' component='h2' sx={sectionTitle}>
          Isi Promo GIIAS BYD 2026
        </Typography>
        <Box
          sx={{
            mt: 3,
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(3, 1fr)' },
            gap: 2,
          }}
        >
          {promoBenefits.map((benefit) => (
            <Box key={benefit.number} sx={benefitCard}>
              <Typography sx={{ color: '#d7a900', fontWeight: 900, letterSpacing: 1 }}>
                {benefit.number}
              </Typography>
              <Typography component='h3' sx={{ mt: 1, fontSize: '1.3rem', fontWeight: 900 }}>
                {benefit.title}
              </Typography>
              <Typography sx={{ ...bodyText, mt: 1, fontSize: '0.96rem' }}>
                {benefit.text}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

function FinancingSection() {
  return (
    <Box component='section' sx={{ px: { xs: 2, md: 6 }, py: { xs: 7, md: 9 }, backgroundColor: '#071a36', color: '#fff' }}>
      <Box
        sx={{
          maxWidth: 1180,
          mx: 'auto',
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: 2,
        }}
      >
        <Box sx={darkCard}>
          <Typography sx={darkEyebrow}>Tenor Fleksibel</Typography>
          <Typography component='h2' sx={darkTitle}>
            Bunga spesial 1-5 tahun
          </Typography>
          <Typography sx={darkText}>
            Rentang tenor memberi ruang untuk menyesuaikan cicilan bulanan.
            Tenor lebih pendek dan tenor lebih panjang memiliki konsekuensi
            berbeda terhadap cicilan serta total pembayaran, sehingga simulasi
            perlu dibandingkan sebelum memilih.
          </Typography>
        </Box>
        <Box sx={{ ...darkCard, borderColor: 'rgba(255,210,63,0.55)' }}>
          <Typography sx={darkEyebrow}>Program Pilihan</Typography>
          <Typography component='h2' sx={darkTitle}>
            Bunga 0%
          </Typography>
          <Typography sx={darkText}>
            Program bunga 0% dapat memiliki ketentuan khusus mengenai model,
            tenor, uang muka, dokumen, dan lembaga pembiayaan. Minta simulasi
            tertulis agar seluruh komponen biaya terlihat jelas sebelum
            melakukan pemesanan.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

function ClaimSection() {
  return (
    <Box component='section' sx={{ px: { xs: 2, md: 6 }, py: { xs: 7, md: 9 }, backgroundColor: '#fff' }}>
      <Box sx={{ maxWidth: 980, mx: 'auto' }}>
        <Typography sx={eyebrow}>Cara Mendapatkan Promo</Typography>
        <Typography component='h2' sx={sectionTitle}>
          Siapkan pilihan sebelum 29 Juli
        </Typography>
        <Box component='ol' sx={{ m: 0, mt: 3, p: 0, listStyle: 'none', display: 'grid', gap: 1.4 }}>
          {claimSteps.map((step, index) => (
            <Box
              component='li'
              key={step}
              sx={{
                display: 'grid',
                gridTemplateColumns: '48px 1fr',
                gap: 1.5,
                alignItems: 'center',
                p: 2,
                borderRadius: '10px',
                border: '1px solid rgba(17,24,39,0.09)',
                backgroundColor: '#f8fafc',
              }}
            >
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  display: 'grid',
                  placeItems: 'center',
                  borderRadius: '10px',
                  color: '#071a36',
                  backgroundColor: '#ffd23f',
                  fontWeight: 900,
                }}
              >
                {index + 1}
              </Box>
              <Typography sx={{ color: '#27364a', lineHeight: 1.65 }}>
                {step}
              </Typography>
            </Box>
          ))}
        </Box>
        <Typography sx={{ mt: 2.5, color: '#6b7280', fontSize: '0.86rem', lineHeight: 1.7 }}>
          *Seluruh program mengikuti syarat dan ketentuan, model, tenor, uang
          muka, stok unit, leasing, dokumen, serta persetujuan kredit yang
          berlaku pada saat transaksi.
        </Typography>
      </Box>
    </Box>
  );
}

function FaqSection() {
  return (
    <Box
      component='section'
      aria-labelledby='faq-giias-title'
      sx={{ px: { xs: 2, md: 6 }, py: { xs: 7, md: 9 } }}
    >
      <Box sx={{ maxWidth: 920, mx: 'auto' }}>
        <Typography id='faq-giias-title' component='h2' sx={sectionTitle}>
          FAQ Promo GIIAS BYD 2026
        </Typography>
        <Box sx={{ mt: 2.5, display: 'grid', gap: 1.3 }}>
          {faqItems.map((item) => (
            <Box
              key={item.question}
              component='details'
              sx={{
                p: 2,
                borderRadius: '10px',
                backgroundColor: '#fff',
                border: '1px solid rgba(17,24,39,0.08)',
                '& summary': {
                  cursor: 'pointer',
                  color: '#111827',
                  fontWeight: 900,
                },
              }}
            >
              <Box component='summary'>{item.question}</Box>
              <Typography sx={{ mt: 1.2, color: '#4b5563', lineHeight: 1.75 }}>
                {item.answer}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

function CtaSection() {
  return (
    <Box component='section' sx={{ px: { xs: 2, md: 6 }, pb: { xs: 7, md: 9 } }}>
      <Box
        sx={{
          maxWidth: 1080,
          mx: 'auto',
          px: { xs: 2.5, md: 5 },
          py: { xs: 4, md: 5 },
          borderRadius: '14px',
          color: '#fff',
          textAlign: 'center',
          background:
            'radial-gradient(circle at 80% 20%, rgba(255,210,63,0.22), transparent 30%), #071a36',
        }}
      >
        <Typography component='h2' sx={{ fontSize: { xs: '2rem', md: '3rem' }, fontWeight: 900, lineHeight: 1.05 }}>
          Cek model yang mendapat promo GIIAS
        </Typography>
        <Typography sx={{ mt: 1.5, maxWidth: 760, mx: 'auto', color: 'rgba(255,255,255,0.78)', lineHeight: 1.8 }}>
          Hubungi Junius untuk membandingkan harga, tenor, bunga 0%, special
          gift, priority unit, merchandise, dan special price pada model BYD
          pilihan Anda.
        </Typography>
        <Button
          href={waLink}
          target='_blank'
          rel='noopener noreferrer'
          variant='contained'
          sx={{ ...buttonPrimary, mt: 3 }}
        >
          Tanya Promo GIIAS
        </Button>
      </Box>
    </Box>
  );
}

const dateBadge = {
  display: 'inline-flex',
  px: 1.3,
  py: 0.7,
  borderRadius: '999px',
  color: '#071a36',
  backgroundColor: '#ffd23f',
  fontSize: '0.8rem',
  fontWeight: 900,
  letterSpacing: 0.5,
  textTransform: 'uppercase',
};

const heroTitle = {
  mt: 1.6,
  maxWidth: 900,
  fontSize: { xs: '2.55rem', sm: '3.7rem', md: '5.2rem' },
  fontWeight: 900,
  lineHeight: 0.98,
  letterSpacing: '-0.045em',
};

const heroText = {
  mt: 1.5,
  maxWidth: 760,
  color: 'rgba(255,255,255,0.9)',
  fontSize: { xs: '1rem', md: '1.12rem' },
  lineHeight: 1.8,
};

const eyebrow = {
  color: '#315b91',
  fontSize: '0.8rem',
  fontWeight: 900,
  letterSpacing: 0.7,
  textTransform: 'uppercase',
};

const sectionTitle = {
  mt: 1,
  color: '#111827',
  fontSize: { xs: '2rem', md: '3rem' },
  fontWeight: 900,
  lineHeight: 1.05,
};

const bodyText = {
  color: '#4b5563',
  fontSize: { xs: '1rem', md: '1.05rem' },
  lineHeight: 1.8,
};

const benefitCard = {
  minHeight: 240,
  p: 2.5,
  borderRadius: '12px',
  border: '1px solid rgba(17,24,39,0.08)',
  backgroundColor: '#fff',
  boxShadow: '0 14px 30px rgba(16,24,40,0.07)',
};

const darkCard = {
  p: { xs: 2.5, md: 3.5 },
  borderRadius: '14px',
  border: '1px solid rgba(255,255,255,0.14)',
  backgroundColor: 'rgba(255,255,255,0.06)',
};

const darkEyebrow = {
  color: '#ffd23f',
  fontSize: '0.78rem',
  fontWeight: 900,
  letterSpacing: 0.8,
  textTransform: 'uppercase',
};

const darkTitle = {
  mt: 1,
  fontSize: { xs: '2rem', md: '3rem' },
  fontWeight: 900,
  lineHeight: 1,
};

const darkText = {
  mt: 1.5,
  color: 'rgba(255,255,255,0.76)',
  lineHeight: 1.8,
};

const buttonPrimary = {
  px: 3.2,
  py: 1.1,
  borderRadius: '999px',
  color: '#071a36',
  backgroundColor: '#ffd23f',
  textTransform: 'none',
  fontWeight: 900,
  '&:hover': { backgroundColor: '#f4c32e' },
};

const buttonGhost = {
  px: 3.2,
  py: 1.1,
  borderRadius: '999px',
  color: '#fff',
  borderColor: 'rgba(255,255,255,0.72)',
  textTransform: 'none',
  fontWeight: 900,
  '&:hover': {
    borderColor: '#fff',
    backgroundColor: 'rgba(255,255,255,0.1)',
  },
};

export default PromoGiias2026Detail;
